// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: things you can do straight from the palette without opening
// anything. "task: fix the login bug" creates a ClickUp task; "remind me
// tomorrow at 9 to call Berno" schedules a reminder. Pure parsing; the store
// runs the action.

export type PaletteAction =
	| {kind: 'task'; name: string}
	| {kind: 'reminder'; at: Date; note: string; whenLabel: string};

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DEFAULT_HOUR = 9;

function parseClock(text: string): {hour: number; minute: number} | null {
	const m = /^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/i.exec(text.trim());
	if (!m) return null;
	let hour = Number(m[1]);
	const minute = m[2] ? Number(m[2]) : 0;
	const suffix = m[3]?.toLowerCase();
	if (hour > 23 || minute > 59) return null;
	if (suffix === 'pm' && hour < 12) hour += 12;
	if (suffix === 'am' && hour === 12) hour = 0;
	return {hour, minute};
}

function at(date: Date, hour: number, minute: number): Date {
	const out = new Date(date);
	out.setHours(hour, minute, 0, 0);
	return out;
}

/** Pull a time expression out of free text; returns the moment and the text without it. */
export function parseWhen(input: string, now: Date): {at: Date; rest: string; label: string} | null {
	let text = ` ${input.trim()} `;
	let when: Date | null = null;
	let label = '';
	const clockRe = '(\\d{1,2}(?:[:.]\\d{2})?\\s*(?:am|pm)?)';
	const take = (re: RegExp, build: (m: RegExpExecArray) => Date | null, lbl: (m: RegExpExecArray) => string) => {
		if (when) return;
		const m = re.exec(text);
		if (!m) return;
		const built = build(m);
		if (!built) return;
		when = built;
		label = lbl(m);
		text = text.replace(m[0], ' ');
	};
	// in 30 min / in 2 hours / in 3 days
	take(
		/\s+in\s+(\d+)\s*(min|mins|minute|minutes|m|h|hr|hrs|hour|hours|d|day|days)\b/i,
		(m) => {
			const n = Number(m[1]);
			const unit = m[2].toLowerCase();
			const ms = unit.startsWith('h') ? n * 3_600_000 : unit.startsWith('d') ? n * 86_400_000 : n * 60_000;
			return new Date(now.getTime() + ms);
		},
		(m) => `in ${m[1]} ${m[2]}`,
	);
	// tomorrow [at 9] / today at 15:00
	take(
		new RegExp(`\\s+(tomorrow|today)(?:\\s+at\\s+${clockRe})?\\s`, 'i'),
		(m) => {
			const base = new Date(now);
			if (m[1].toLowerCase() === 'tomorrow') base.setDate(base.getDate() + 1);
			const clock = m[2] ? parseClock(m[2]) : {hour: DEFAULT_HOUR, minute: 0};
			return clock ? at(base, clock.hour, clock.minute) : null;
		},
		(m) => `${m[1].toLowerCase()}${m[2] ? ` at ${m[2]}` : ''}`,
	);
	// on friday / next monday at 10
	take(
		new RegExp(`\\s+(?:on\\s+|next\\s+)?(${DAYS.join('|')})(?:\\s+at\\s+${clockRe})?\\s`, 'i'),
		(m) => {
			const target = DAYS.indexOf(m[1].toLowerCase());
			const base = new Date(now);
			let ahead = (target - base.getDay() + 7) % 7;
			if (ahead === 0) ahead = 7;
			base.setDate(base.getDate() + ahead);
			const clock = m[2] ? parseClock(m[2]) : {hour: DEFAULT_HOUR, minute: 0};
			return clock ? at(base, clock.hour, clock.minute) : null;
		},
		(m) => `${m[1]}${m[2] ? ` at ${m[2]}` : ''}`,
	);
	// at 15:00 (today, or tomorrow if that has passed)
	take(
		new RegExp(`\\s+at\\s+${clockRe}\\s`, 'i'),
		(m) => {
			const clock = parseClock(m[1]);
			if (!clock) return null;
			const candidate = at(now, clock.hour, clock.minute);
			if (candidate.getTime() <= now.getTime()) candidate.setDate(candidate.getDate() + 1);
			return candidate;
		},
		(m) => `at ${m[1]}`,
	);
	if (!when) return null;
	const rest = text
		.replace(/\s+/g, ' ')
		.replace(/^\s*(to|about|that|of)\s+/i, '')
		.trim();
	return {at: when, rest, label};
}

export function parsePaletteAction(query: string, now = new Date()): PaletteAction | null {
	const text = query.trim();
	const task = /^(?:task|todo)\s*:\s*(.+)$/i.exec(text);
	if (task) {
		const name = task[1].trim();
		return name ? {kind: 'task', name} : null;
	}
	const remind = /^remind(?:\s+me)?\b\s*(.*)$/i.exec(text);
	if (remind) {
		const body = remind[1].trim();
		const when = parseWhen(body, now) ?? {
			at: at(new Date(now.getTime() + 86_400_000), DEFAULT_HOUR, 0),
			rest: body.replace(/^\s*(to|about|that|of)\s+/i, '').trim(),
			label: 'tomorrow at 9',
		};
		return {kind: 'reminder', at: when.at, note: when.rest || 'Reminder', whenLabel: when.label};
	}
	return null;
}

export function formatReminderTime(date: Date, now = new Date()): string {
	const sameDay = date.toDateString() === now.toDateString();
	const tomorrow = new Date(now);
	tomorrow.setDate(tomorrow.getDate() + 1);
	const day = sameDay
		? 'today'
		: date.toDateString() === tomorrow.toDateString()
			? 'tomorrow'
			: date.toLocaleDateString([], {weekday: 'short', day: 'numeric', month: 'short'});
	return `${day} ${date.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`;
}
